import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { vendorService, Vendor, VendorFormData } from '../../services/vendorService';

interface VendorState {
  vendors: Vendor[];
  currentVendor: Vendor | null;
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  previousPage: number | null;
  nextPage: number | null;
}

const initialState: VendorState = {
  vendors: [],
  currentVendor: null,
  isLoading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  totalPages: 0,
  previousPage: null,
  nextPage: null,
};

// Async thunks
export const fetchVendors = createAsyncThunk(
  'vendors/fetchVendors',
  async ({ page = 1, limit = 10 }: { page?: number; limit?: number } = {}) => {
    const response = await vendorService.getVendors(page, limit);
    return response;
  }
);

export const fetchVendorById = createAsyncThunk(
  'vendors/fetchVendorById',
  async (id: string) => {
    const response = await vendorService.getVendorById(id);
    return response.data;
  }
);

export const createVendor = createAsyncThunk(
  'vendors/createVendor',
  async (vendorData: VendorFormData, { rejectWithValue }) => {
    try {
      const response = await vendorService.createVendor(vendorData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create vendor');
    }
  }
);

export const updateVendor = createAsyncThunk(
  'vendors/updateVendor',
  async ({ id, vendorData }: { id: string; vendorData: VendorFormData }, { rejectWithValue }) => {
    try {
      const response = await vendorService.updateVendor(id, vendorData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update vendor');
    }
  }
);

export const deleteVendor = createAsyncThunk(
  'vendors/deleteVendor',
  async (id: string, { rejectWithValue }) => {
    try {
      await vendorService.deleteVendor(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to delete vendor');
    }
  }
);

const vendorSlice = createSlice({
  name: 'vendors',
  initialState,
  reducers: {
    clearVendorError: (state) => {
      state.error = null;
    },
    clearCurrentVendor: (state) => {
      state.currentVendor = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch vendors
      .addCase(fetchVendors.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVendors.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vendors = action.payload.data.result;
        state.totalCount = action.payload.data.totalCount;
        state.currentPage = action.payload.data.currentPage;
        state.totalPages = action.payload.data.totalPages;
        state.previousPage = action.payload.data.previousPage;
        state.nextPage = action.payload.data.nextPage;
      })
      .addCase(fetchVendors.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch vendors';
      })
      // Fetch vendor by ID
      .addCase(fetchVendorById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVendorById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentVendor = action.payload;
      })
      .addCase(fetchVendorById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch vendor';
      })
      // Create vendor
      .addCase(createVendor.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createVendor.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vendors.unshift(action.payload);
        state.totalCount += 1;
      })
      .addCase(createVendor.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to create vendor';
      })
      // Update vendor
      .addCase(updateVendor.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateVendor.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.vendors.findIndex(vendor => vendor._id === action.payload._id);
        if (index !== -1) {
          state.vendors[index] = action.payload;
        }
        if (state.currentVendor && state.currentVendor._id === action.payload._id) {
          state.currentVendor = action.payload;
        }
      })
      .addCase(updateVendor.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update vendor';
      })
      // Delete vendor
      .addCase(deleteVendor.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteVendor.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vendors = state.vendors.filter(vendor => vendor._id !== action.payload);
        state.totalCount -= 1;
        if (state.currentVendor && state.currentVendor._id === action.payload) {
          state.currentVendor = null;
        }
      })
      .addCase(deleteVendor.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to delete vendor';
      });
  },
});

export const { clearVendorError, clearCurrentVendor } = vendorSlice.actions;
export default vendorSlice.reducer;